import type { InjectionKey, ComputedRef } from "vue";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { CanvasDimensions } from "./CanvasDimensions";
import type { FlowItem } from "@editor/items/flow/FlowItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";
import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";
import type { NetEmits, RawNetCanvasProps } from "./NetCanvasProps";
import type { SceneTreeItemSize } from "@editor/items/scene/SceneTreeItem";
import type { Uid } from "@/helpers/utility/Id";

export type NetItems = {
  target: {
    [targetId: number]: TargetItem;
  };
  resource: {
    [resourceId: number]: ResourceItem;
  };
  index: {
    [indexId: number]: IndexItem;
  };
  flow: {
    [flowId: number]: FlowItem;
  }
};

export const keyTargets = Symbol() as InjectionKey<
  ComputedRef<{ [targetId: number]: TargetItem }>
>;
export const keyResources = Symbol() as InjectionKey<
  ComputedRef<{ [resourceId: number]: ResourceItem }>
>;
export const keyIndices = Symbol() as InjectionKey<
  ComputedRef<{ [indexId: number]: IndexItem }>
>;
export const keyFlows = Symbol() as InjectionKey<
  ComputedRef<{ [flowId: number]: FlowItem }>
>;
export const keyFlowBranches = Symbol() as InjectionKey<
  ComputedRef<{ [flowId: number]: FlowBranchItem }>
>;

export const keyNetItems = Symbol() as InjectionKey<ComputedRef<NetItems>>;
export const keyItemSizes = Symbol() as InjectionKey<
  ComputedRef<{ [uid: Uid<"sceneNode" | "flow">]: SceneTreeItemSize }>
>;

export const keyNetScale = Symbol() as InjectionKey<ComputedRef<number>>;

export const keyCanvasDimensions = Symbol() as InjectionKey<
  ComputedRef<CanvasDimensions>
>;

export const keyElementValues = Symbol() as InjectionKey<ComputedRef<{ [uid: string]: ExpressionValue }>>;

export const keyRoundIndex = Symbol() as InjectionKey<ComputedRef<number>>;

export const keyNetId = Symbol() as InjectionKey<ComputedRef<string>>;

export const keyNetCanvas = Symbol() as InjectionKey<ComputedRef<{ props: RawNetCanvasProps, emit: NetEmits, transform: { translateX: number; translateY: number, scale: number } }>>;